import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCocktailOfTheWeek } from "@/lib/api";
import { useSavedMatches } from "@/hooks/useSavedMatches";
import { printCocktail } from "@/lib/printCocktail";
import { Heart, Printer, ArrowRight, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function CocktailOfTheWeek() {
  const [data, setData] = useState(null);
  const { isSaved, toggle } = useSavedMatches();

  useEffect(() => {
    fetchCocktailOfTheWeek().then(setData).catch(() => {});
  }, []);

  if (!data) return null;
  const c = data.cocktail;
  const saved = isSaved(c.slug);

  const handleSave = () => {
    toggle(c.slug);
    toast.success(saved ? "Removed from saves" : `Saved ${c.name} to your bar cart`);
  };

  return (
    <section className="bg-ink border-y border-cream/10 py-14 sm:py-20" data-testid="cocktail-of-the-week">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
        <div className="lg:col-span-5 relative order-2 lg:order-1">
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-2xl">
            <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/20" />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 h-9 px-4 rounded-full bg-ink text-cream text-[10px] font-bold uppercase tracking-[0.25em] border border-cream/20">
              <Sparkles size={12} className="text-pink-brand" /> Week {data.week_number} · {data.year}
            </div>
            <div className="absolute bottom-4 right-4 h-16 w-16 rounded-full bg-pink text-cream grid place-items-center font-serif-editor italic text-xs shadow-lg text-center leading-tight rotate-[-6deg]">
              Editor's<br />Pick
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <p className="eyebrow">Cocktail of the Week</p>
          <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl font-bold text-cream leading-[1.02]">
            Meet <span className="italic font-serif-editor text-pink-brand font-medium">{c.name}.</span>
          </h2>
          <p className="mt-5 font-serif-editor italic text-lg sm:text-xl text-cream/85 max-w-xl leading-relaxed">
            {data.editors_note}
          </p>
          <p className="mt-5 text-cream/70 font-body max-w-xl">{c.description}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            <span className="chip chip-pink">{c.base_spirit}</span>
            <span className="chip chip-ink">{c.strength}</span>
            <span className="chip chip-ink">ABV {c.abv}</span>
            {c.taste_profile.slice(0, 2).map(t => <span key={t} className="chip chip-pink">{t}</span>)}
            <span className="flex items-center gap-1 chip chip-ink"><Star size={12} className="text-pink-brand" fill="currentColor" /> {c.beginner_rating}/5</span>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            <div>
              <p className="eyebrow">Ingredients</p>
              <ul className="mt-2 space-y-1 text-sm text-cream/85 font-body">
                {c.ingredients.slice(0, 5).map((i, k) => <li key={k}>· {i}</li>)}
              </ul>
            </div>
            <div>
              <p className="eyebrow">Sip Alchemy Pro Tip</p>
              <p className="mt-2 font-serif-editor italic text-cream/80 text-sm sm:text-base leading-relaxed">{c.pro_tip}</p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/cocktails" className="btn-pill btn-pink" data-testid="cotw-view-cocktails">
              See full recipe <ArrowRight size={16} />
            </Link>
            <button onClick={handleSave} className={`btn-pill ${saved ? "btn-pink" : "btn-outline-ink"}`} data-testid="cotw-save">
              <Heart size={16} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save it"}
            </button>
            <button onClick={() => printCocktail(c)} className="btn-pill btn-outline-ink" data-testid="cotw-print">
              <Printer size={16} /> Print
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
